import * as mock from './mockApi.js';

const BASE = import.meta.env.VITE_API_BASE || '/api';

/**
 * Preview builds swap the network layer for an in-browser mock so the journey
 * can be shared and reviewed without a server. Everything above this line —
 * every component, every piece of validation and state — is the real thing.
 */
export const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true';

export class ApiError extends Error {
  constructor(message, { status, code, details } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    // Normalised to an array: the server sends a list for validation failures
    // but a single object for some field-scoped errors, and `fieldErrors` is
    // read inside catch blocks where a throw would swallow the message
    // entirely and leave the customer staring at nothing.
    this.details = Array.isArray(details) ? details : details ? [details] : [];
  }

  /** Turn server-side validation errors into a `{ field: message }` map. */
  get fieldErrors() {
    return Object.fromEntries(
      this.details.filter((d) => d.field).map((d) => [d.field, d.message]),
    );
  }
}

async function request(path, { method = 'GET', body, signal } = {}) {
  let res;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (err) {
    if (err.name === 'AbortError') throw err;
    // A network failure is the one error a customer can act on themselves.
    throw new ApiError('We could not reach our servers. Please check your connection and try again.', {
      status: 0,
      code: 'network_error',
    });
  }

  const payload = await res.json().catch(() => ({}));

  if (!res.ok) {
    const error = payload?.error ?? {};
    throw new ApiError(error.message || 'Something went wrong. Please try again.', {
      status: res.status,
      code: error.code,
      details: error.details,
    });
  }

  return payload;
}

export const getJourney = (slug, signal) =>
  USE_MOCK
    ? mock.getJourney(slug).catch((e) => {
        throw new ApiError(e.message, { status: e.status, code: 'not_found' });
      })
    : request(`/journeys/${encodeURIComponent(slug)}`, { signal });

export const lookupPostcode = (postcode, signal) =>
  USE_MOCK
    ? mock.lookupPostcode(postcode)
    : request(`/address/lookup?postcode=${encodeURIComponent(postcode)}`, { signal });

export const submitLead = (slug, body) =>
  USE_MOCK
    ? mock.submitLead(slug, body)
    : request(`/journeys/${encodeURIComponent(slug)}/leads`, { method: 'POST', body });

/**
 * Contact-page enquiry. Stored in `contact_enquiries` server-side and emailed
 * on once SendGrid is configured; until then it is recorded and stays pending,
 * so nothing a customer sends is lost.
 */
export const submitEnquiry = (body) =>
  USE_MOCK ? mock.submitEnquiry(body) : request('/contact', { method: 'POST', body });

/**
 * Funnel tracking. Deliberately fire-and-forget: analytics must never delay a
 * customer or surface an error to them.
 */
export function trackStep(body) {
  if (USE_MOCK) return;
  request('/track', { method: 'POST', body }).catch(() => {});
}
